import React, { useState, lazy, Suspense, useEffect } from 'react';
import { updateAdventurerPreference, setSupabaseToken, getProfileV3, clearParentElevation, setElevationPrompt, listChildrenV3 } from './api/client';
import type { ChildSummary } from './api/client';
import ChildPicker from './components/ChildPicker';
import type { StudentProfile } from './api/types';
import type { AdventurerId } from './components/adventurer/AdventurerStamp';
import { supabase } from './api/supabase';
import type { Session } from '@supabase/supabase-js';
import LoginScreen from './screens/LoginScreen';
import RegisterScreen from './screens/RegisterScreen';
import AdventurerSelectScreen from './screens/AdventurerSelectScreen';
import StoryScreen from './screens/StoryScreen';
import ExplainerScreen from './screens/ExplainerScreen';
import SummaryScreen from './screens/SummaryScreen';
import SettingsScreen from './screens/SettingsScreen';
import LearnModeScreen from './screens/LearnModeScreen';
import { PasswordResetScreen } from './screens/PasswordResetScreen';
import ParentGate from './components/parent/ParentGate';
import Logo from './components/brand/Logo';

// Lazy-load the parent dashboard so recharts doesn't bloat the main bundle
const ParentDashboardScreen = lazy(() => import('./screens/ParentDashboardScreen'));

type Screen =
  | 'verifying'
  | 'login'
  | 'complete_registration' // Supabase-invited user setting up their family for the first time
  | 'password_reset'    // /password-reset request form
  | 'password_recovery' // user clicked the reset link — show set-new-password form
  | 'adventurer_select'
  | 'story'
  | 'child_select'      // "Wer übt heute?" — families with more than one child
  | 'explain'
  | 'summary'
  | 'parent_dashboard'
  | 'settings'
  | 'learn';

function initialScreenFromUrl(): Screen | null {
  const path = window.location.pathname;
  if (path.startsWith('/password-reset')) return 'password_reset';
  // /register lands here from a Supabase invite magic-link. The auth listener
  // resolves the rest — if signed in with no family, completeMode; if signed
  // in with a family, /story; if not signed in, /login.
  return null;
}

// The picker shows once per browser session after login; the "Kind wechseln"
// button reopens it. Storage can be unavailable (private mode).
const CHILD_PICKED_KEY = 'finnly-child-picked';

function childPickedThisSession(): boolean {
  try { return sessionStorage.getItem(CHILD_PICKED_KEY) === '1'; } catch { return false; }
}

function markChildPicked(): void {
  try { sessionStorage.setItem(CHILD_PICKED_KEY, '1'); } catch { /* ignore */ }
}

function arcForProfile(profile: StudentProfile): string {
  // Use the canonical current_arc_id from the backend StoryState. The legacy
  // current_chapter string-keyword routing only knew about three arcs and
  // would silently fall through to "schatzsuche" for any kid whose
  // current_chapter wasn't populated (the field is deprecated).
  return profile.story_state?.current_arc_id || 'schatzsuche';
}

interface PendingGate {
  purpose: string;
  onPass: () => void;
  onCancel?: () => void;
}

export default function App() {
  const [screen, setScreen] = useState<Screen>(() => {
    const fromUrl = initialScreenFromUrl();
    if (fromUrl) return fromUrl;
    // Always start on verifying so the Supabase auth listener gets a chance
    // to restore a stored session before we drop the user on the login screen.
    return 'verifying';
  });
  const [profile, setProfile] = useState<StudentProfile | null>(null);
  const [adventurerId, setAdventurerId] = useState<AdventurerId>('finn');
  const [explainTopic, setExplainTopic] = useState<string>('');
  const [parentReturn, setParentReturn] = useState<'story' | 'summary'>('story');
  const [pendingGate, setPendingGate] = useState<PendingGate | null>(null);
  const [completeRegEmail, setCompleteRegEmail] = useState<string>('');
  const [children, setChildren] = useState<ChildSummary[]>([]);

  /** Load the family's children; returns the screen to show after sign-in. */
  const loadChildren = async (): Promise<Screen> => {
    try {
      const { children: list } = await listChildrenV3();
      setChildren(list);
      return list.length > 1 && !childPickedThisSession() ? 'child_select' : 'story';
    } catch {
      return 'story';  // the picker is a convenience; never block the story on it
    }
  };

  const gated = (purpose: string, action: () => void) => () => {
    setPendingGate({ purpose, onPass: action });
  };

  // When a parent-only request finds the 5-minute gate expired, the API
  // client asks here: re-open the ParentGate and resolve with the outcome.
  useEffect(() => {
    setElevationPrompt(purpose => new Promise<boolean>(resolve => {
      setPendingGate({ purpose, onPass: () => resolve(true), onCancel: () => resolve(false) });
    }));
    return () => setElevationPrompt(null);
  }, []);

  // Supabase auth state listener — handles sign-in, recovery, and sign-out events.
  useEffect(() => {
    // Runs outside the auth callback: supabase-js holds an internal lock while
    // it emits events, and awaiting another auth call inside the callback
    // (getUser, signOut) can deadlock token refresh.
    async function finishSignIn(session: Session) {
      // Validate the session against Supabase's live auth.users (not just
      // local JWT signature). Catches the case where the user was deleted
      // server-side but the JWT still lives in this browser's localStorage —
      // without this check we'd send them to the complete-registration form
      // as if they were a fresh invitee.
      const { data: liveUser, error: liveErr } = await supabase.auth.getUser();
      if (liveErr || !liveUser?.user) {
        await supabase.auth.signOut().catch(() => {});
        setSupabaseToken(null);
        setScreen('login');
        return;
      }

      try {
        const { profile: p } = await getProfileV3();
        // Returning user with an existing family — straight to the app.
        window.history.replaceState({}, '', '/');
        setProfile(p);
        setAdventurerId((p.story_state?.adventurer_id as AdventurerId) || 'finn');
        setScreen(await loadChildren());
      } catch {
        // Authenticated but no family yet — this is a freshly-invited
        // parent. Show the one-shot intake + password setup form.
        setCompleteRegEmail(session.user?.email ?? '');
        window.history.replaceState({}, '', '/');
        setScreen('complete_registration');
      }
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Keep the API client's Bearer token current. Without this, requests
      // keep sending the old token after the hourly refresh and fail with 401.
      if ((event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') && session) {
        setSupabaseToken(session.access_token);
        return;
      }

      if (event === 'PASSWORD_RECOVERY') {
        window.history.replaceState({}, '', '/password-reset');
        setScreen('password_recovery');
        return;
      }

      // Treat INITIAL_SESSION (emitted when Supabase restores a live session
      // on mount — e.g. after the user clicked an invite/verification link)
      // the same way as SIGNED_IN.
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session) {
        setSupabaseToken(session.access_token);

        // Password-recovery clicks ALSO fire SIGNED_IN/INITIAL_SESSION before
        // PASSWORD_RECOVERY. Without this guard the user gets bounced into
        // /story before the set-new-password form ever renders. The hash
        // fragment Supabase appends carries `type=recovery`; we also accept
        // a recovery URL path in case the fragment was already consumed.
        const hash = window.location.hash || '';
        const path = window.location.pathname || '';
        if (hash.includes('type=recovery') || path.startsWith('/password-reset')) {
          window.history.replaceState({}, '', '/password-reset');
          setScreen('password_recovery');
          return;
        }

        setTimeout(() => { void finishSignIn(session); }, 0);
        return;
      }

      if (event === 'SIGNED_OUT') {
        setSupabaseToken(null);
        clearParentElevation();
        setProfile(null);
        setScreen('login');
      }
    });
    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On startup, resolve the Supabase session → story (or login if none).
  useEffect(() => {
    if (screen !== 'verifying') return;
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session) {
        setScreen('login');
        return;
      }
      // The SIGNED_IN / INITIAL_SESSION handler above takes care of routing
      // (story vs complete_registration). Just ensure the API client has the
      // token; the listener fires with INITIAL_SESSION shortly after.
      setSupabaseToken(session.access_token);
    });
  }, []);

  const handleLogin = async (p: StudentProfile) => {
    setProfile(p);
    setAdventurerId((p.story_state?.adventurer_id as AdventurerId) || 'finn');
    setScreen(await loadChildren());
  };

  const handleChildPicked = (p: StudentProfile) => {
    markChildPicked();
    setProfile(p);
    setAdventurerId((p.story_state?.adventurer_id as AdventurerId) || 'finn');
    setScreen('story');
  };

  const handleAdventurerSelect = async (id: AdventurerId) => {
    setAdventurerId(id);
    try {
      await updateAdventurerPreference({ adventurer_id: id });
      setProfile((p) => p ? {
        ...p,
        adventurer_id: id,
        story_state: { ...p.story_state, adventurer_id: id },
      } : p);
    } catch (e) {
      console.error('Failed to persist adventurer choice', e);
    }
    setScreen('story');
  };

  const handleTriggerExplain = (topicKey: string) => {
    setExplainTopic(topicKey);
    setScreen('explain');
  };

  const handleLogout = () => {
    try { sessionStorage.removeItem(CHILD_PICKED_KEY); } catch { /* ignore */ }
    supabase.auth.signOut().catch(() => {});
    setProfile(null);
    setScreen('login');
  };

  let body: React.ReactElement;
  if (screen === 'verifying') {
    body = (
      <div style={{
        display: 'flex', flexDirection: 'column', height: '100dvh',
        alignItems: 'center', justifyContent: 'center', gap: 20,
        background: 'var(--parchment)',
      }}>
        <Logo variant="lockup" height={140} className="logo-ear-wiggle" />
        <div style={{
          fontFamily: 'var(--serif)', fontSize: 20,
          fontStyle: 'italic', color: 'var(--ink-muted)',
          marginTop: 4,
        }}>Finn bereitet sich vor…</div>
      </div>
    );
  } else if (screen === 'login') {
    body = (
      <LoginScreen
        onLogin={handleLogin}
        onForgotPassword={() => setScreen('password_reset')}
      />
    );
  } else if (screen === 'complete_registration') {
    body = (
      <RegisterScreen
        onBack={() => {
          // "Back" here logs them out; they have no family so there's nowhere else to go.
          supabase.auth.signOut().catch(() => {});
          setScreen('login');
        }}
        completeMode={{
          email: completeRegEmail,
          onDone: (p) => {
            setProfile(p);
            setAdventurerId((p.story_state?.adventurer_id as AdventurerId) || 'finn');
            setScreen('adventurer_select');
          },
        }}
      />
    );
  } else if (screen === 'password_reset') {
    body = (
      <PasswordResetScreen
        onDone={() => { window.history.replaceState({}, '', '/'); setScreen('login'); }}
      />
    );
  } else if (screen === 'password_recovery') {
    body = (
      <PasswordResetScreen
        isRecovery
        onDone={() => { window.history.replaceState({}, '', '/'); setScreen('login'); }}
      />
    );
  } else if (screen === 'adventurer_select') {
    body = (
      <AdventurerSelectScreen
        childName={profile?.name ?? ''}
        onSelect={handleAdventurerSelect}
      />
    );
  } else if (screen === 'explain') {
    body = (
      <ExplainerScreen
        topicKey={explainTopic}
        adventurerId={adventurerId}
        onDone={() => setScreen('story')}
      />
    );
  } else if (screen === 'summary') {
    body = (
      <SummaryScreen
        profile={profile}
        adventurerId={adventurerId}
        onPlayAgain={() => setScreen('story')}
        onLogout={gated('logout', handleLogout)}
        onDashboard={gated('open_parent_dashboard', () => { setParentReturn('summary'); setScreen('parent_dashboard'); })}
      />
    );
  } else if (screen === 'child_select') {
    body = (
      <ChildPicker
        children={children}
        activeId={profile?.student_id}
        onPicked={handleChildPicked}
        onCancel={childPickedThisSession() && profile ? () => setScreen('story') : undefined}
      />
    );
  } else if (screen === 'settings') {
    body = (
      <SettingsScreen
        profile={profile}
        onChildAdded={() => { void loadChildren(); }}
        onBack={() => setScreen('story')}
        onSaved={(updated) => {
          setAdventurerId(updated.adventurer_id as AdventurerId);
          setProfile((p) => p ? {
            ...p,
            adventurer_id: updated.adventurer_id,
            story_state: { ...p.story_state, adventurer_id: updated.adventurer_id },
            preferences: updated.preferences,
            interests: updated.interests,
          } : p);
          setScreen('story');
        }}
      />
    );
  } else if (screen === 'learn') {
    body = (
      <LearnModeScreen
        adventurerId={adventurerId}
        onBack={() => setScreen('story')}
      />
    );
  } else if (screen === 'parent_dashboard') {
    body = (
      <Suspense fallback={
        <div style={{ display: 'flex', height: '100dvh', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--serif)', fontSize: 18, fontStyle: 'italic', color: 'var(--ink-muted)' }}>
          Wird geladen…
        </div>
      }>
        <ParentDashboardScreen
          profile={profile}
          adventurerId={adventurerId}
          onBack={() => setScreen(parentReturn)}
        />
      </Suspense>
    );
  } else {
    body = (
      <StoryScreen
        key={profile?.student_id ?? 'none'}
        adventurerId={adventurerId}
        arc={profile ? arcForProfile(profile) : 'schatzsuche'}
        profile={profile}
        onSessionComplete={() => setScreen('summary')}
        onTriggerExplain={handleTriggerExplain}
        onSessionExpired={handleLogout}
        onParentDashboard={gated('open_parent_dashboard', () => { setParentReturn('story'); setScreen('parent_dashboard'); })}
        onSettings={gated('open_settings', () => setScreen('settings'))}
        onLearnMode={() => setScreen('learn')}
        onSwitchChild={children.length > 1 ? () => setScreen('child_select') : undefined}
        onLogout={gated('logout', handleLogout)}
      />
    );
  }

  return (
    <>
      {body}
      {pendingGate && (
        <ParentGate
          purpose={pendingGate.purpose}
          klasse={profile?.klasse ?? 2}
          onPass={() => {
            const action = pendingGate.onPass;
            setPendingGate(null);
            action();
          }}
          onCancel={() => {
            pendingGate.onCancel?.();
            setPendingGate(null);
          }}
        />
      )}
    </>
  );
}
